import type { Node } from 'react'
import React, { Fragment, useEffect, useState, useRef } from 'react'
import { Form, Formik } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import styled from 'styled-components'
import { ButtonLarge, media, style } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { SmallText, Text, TinyText } from 'app/common/typography'
import { CheckOrRadio, Error, Input, Select, Textarea } from 'app/common/forms'
import { boolean, mixed, object, string } from 'yup'
import type { RestaurantFaqShortDTO } from 'shared/types'
import { hoursOptions } from './hoursOptions'
import { notesOptions } from './notesOptions'
import {
    ecommerceType,
    formatDate,
    getPath,
    isPastedDate,
    regex
} from 'shared/utils'
import { Link } from 'app/common/link'
import {
    Checkbox,
    CircleChecked,
    CirclePlus,
    EmptyCircle,
    RadioChecked
} from 'app/assets/icons'
import { Notes } from './Notes'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import { fetchProfileData } from 'shared/store/kingdom/profile'
import { sendFaqFormData } from 'shared/store/faq'
import { faqFormSubject } from 'shared/utils/const'

const { grey, yellow, red, tertiary } = colors
const { CLICK_AND_COLLECT, DELIVERY_IN_APP, EXTERNAL_DELIVERY } = ecommerceType

type ReceiptProps = {
    labelKey: string,
    children?: Node,
    intl: IntlShape
}

const Receipt = (props: ReceiptProps) => {
    const { labelKey, children, intl } = props
    return (
        <Fragment>
            {children}
            <SmallText>
                {intl.formatMessage({
                    id: labelKey
                })}
            </SmallText>
        </Fragment>
    )
}

type Props = {
    selectEcommerceMode: (ecommerceMode: string) => void,
    ecommerceMode: string
}

let fileUpload: ?HTMLInputElement

const FaqForm = (props: Props) => {
    const dispatch = useDispatch()
    const intl = useIntl()
    const { ecommerceMode, selectEcommerceMode } = props
    const {
        authenticated,
        selectedTheme,
        formData,
        success,
        email,
        civility,
        lastname,
        firstname,
        phone,
        sendFormError
    } = useSelector((state: RootState) => ({
        authenticated: state.userState.authenticated,
        email: state.kingdomProfilePageState.email,
        civility: state.kingdomProfilePageState.civility,
        lastname: state.kingdomProfilePageState.lastname,
        firstname: state.kingdomProfilePageState.firstname,
        phone: state.kingdomProfilePageState.phone,
        error: state.faqPageState.error,
        sendFormError: state.faqPageState.sendFormError,
        selectedTheme: state.faqPageState.selectedTheme || {},
        formData: state.faqPageState.formData,
        success: state.faqPageState.success
    }))
    const [fileUploaded, setFileUploaded] = useState(false)
    const myRef = useRef()

    const isClickAndCollect =
        selectedTheme?.ecommerce && ecommerceMode === CLICK_AND_COLLECT
    const isDeliveryInApp =
        selectedTheme?.ecommerce && ecommerceMode === DELIVERY_IN_APP
    const isExternalDelivery =
        selectedTheme?.ecommerce && ecommerceMode === EXTERNAL_DELIVERY

    const manKey = 'form.civility.man'
    const man = intl.formatMessage({ id: 'form.civility.man' })
    const womanKey = 'form.civility.woman'
    const woman = intl.formatMessage({ id: 'form.civility.woman' })
    const kingdomPointsSubjectKey = 'form.subject.kingdom.points'
    const otherSubjectKey = 'form.subject.kingdom.other'
    const legalNoticeMail = intl.formatMessage({
        id: 'faq.form.legalNotice.mail'
    })

    const initialValues: any = {
        themeLabel: selectedTheme ? selectedTheme.label : '',
        civility: civility,
        lastname,
        firstname,
        address: '',
        zipCode: '',
        city: '',
        email,
        loyaltyCardCode: '',
        phone,
        subject: '',
        message: '',
        note: '',
        department: '-1',
        restaurant: '',
        date: '',
        hour: '070000',
        paymentType: '',
        orderType: '',
        amount: '',
        receipt: null,
        agreements: false
    }

    useEffect(() => {
        authenticated && dispatch(fetchProfileData())
    }, [])

    useEffect(() => {
        if (success) {
            let scrollTop = 0
            if (document.documentElement != null) {
                scrollTop =
                    window.pageYOffset || document.documentElement.scrollTop
            }
            if (myRef.current) {
                const top = myRef.current.getBoundingClientRect().top
                window.scrollTo(0, scrollTop + top - 100)
            }
        }
    }, [success])

    const filterRestaurants = (
        restaurants: Array<RestaurantFaqShortDTO>,
        departmentCode: string
    ) =>
        restaurants
            .filter(
                (r: RestaurantFaqShortDTO) =>
                    r.departmentCode === departmentCode ||
                    departmentCode === '-1'
            )
            .map((r: RestaurantFaqShortDTO) => {
                return {
                    code: r.id,
                    label: r.label
                }
            })

    const handleFileChange = async (
        e: SyntheticEvent<HTMLInputElement>,
        setFieldValue: (string, Object) => void
    ) => {
        if (e.currentTarget.files.length === 0) {
            return
        }
        const file = e.currentTarget.files[0]
        try {
            setFieldValue('receipt', {
                file: file,
                size: file.size,
                extension: file.type.split('/')[1]
            })
            setFileUploaded(true)
        } catch (e) {
            setFileUploaded(false)
            console.error(e)
        }
    }

    const handleSelectChange = (
        e: SyntheticEvent<HTMLSelectElement>,
        name: string,
        setFieldValue: (string, string) => void
    ) => {
        setFieldValue(name, e.currentTarget.value)
    }

    const handleDepartmentChange = (
        e: SyntheticEvent<HTMLSelectElement>,
        setFieldValue: (string, string) => void
    ) => {
        setFieldValue('restaurant', '')
        setFieldValue('department', e.currentTarget.value)
    }

    const handleRestaurantChange = (
        e: SyntheticEvent<HTMLSelectElement>,
        setFieldValue: (string, string) => void
    ) => {
        const restaurantId = e.currentTarget.value
        const departmentCode =
            findDepartmentCodeByRestaurantId(restaurantId) || ''
        setFieldValue('restaurant', restaurantId)
        setFieldValue('department', departmentCode)
    }

    const findDepartmentCodeByRestaurantId = (rId: string): ?string => {
        const restaurant: ?RestaurantFaqShortDTO = formData.restaurants.find(
            (r: RestaurantFaqShortDTO) => r.id === rId
        )
        return restaurant ? restaurant.departmentCode : undefined
    }

    const formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        const invalidKey = 'faq.form.error.invalid'
        const invalid = {
            message: invalidKey,
            excludeEmptyString: true
        }
        return object().shape({
            note: string().test(
                'isRequiredNote',
                mandatory,
                value =>
                    (selectedTheme?.notation && value && value !== '-1') ||
                    !selectedTheme?.notation
            ),
            civility: string().required(mandatory),
            lastname: string().required(mandatory),
            firstname: string().required(mandatory),
            email: string()
                .required(mandatory)
                .matches(regex.EMAIL, invalid),
            loyaltyCardCode: string()
                .test(
                    'isRequiredLoyaltyCardCode',
                    mandatory,
                    value =>
                        (selectedTheme?.kingdom && value) ||
                        !selectedTheme?.kingdom
                )
                .test(
                    'isValidLoyaltyCardCode',
                    invalidKey,
                    value =>
                        (selectedTheme?.kingdom &&
                            value &&
                            value.match(regex.LOYALTY_CARD)) ||
                        !selectedTheme?.kingdom
                ),
            address: string().test(
                'isRequiredAddress',
                mandatory,
                value =>
                    (selectedTheme.restaurant && value && value !== '-1') ||
                    !selectedTheme.restaurant
            ),
            zipCode: string()
                .test(
                    'isRequiredZipCode',
                    mandatory,
                    value =>
                        (selectedTheme.restaurant && value && value !== '-1') ||
                        !selectedTheme.restaurant
                )
                .matches(regex.ZIP_CODE, invalid),
            city: string().test(
                'isRequiredCity',
                mandatory,
                value =>
                    (selectedTheme.restaurant && value && value !== '-1') ||
                    !selectedTheme.restaurant
            ),
            phone: string()
                .required(mandatory)
                .matches(regex.TEL, invalid),
            subject: string().test(
                'isRequiredSubject',
                mandatory,
                value =>
                    (selectedTheme.kingdom && value) || !selectedTheme.kingdom
            ),
            message: string().required(mandatory),
            restaurant: string().test(
                'isRequiredRestaurant',
                mandatory,
                value =>
                    (selectedTheme.restaurant && value && value !== '-1') ||
                    !selectedTheme.restaurant
            ),
            date: string().test('isValidDate', invalidKey, value => {
                if (value === undefined) {
                    return true
                }
                if (value) {
                    if (value.match(regex.DATE)) {
                        const date = value.split('/')
                        const dateFormatted = `${date[2]}-${date[1]}-${date[0]}`
                        return isPastedDate(dateFormatted)
                    }
                    if (value.match(regex.DATE_ISO)) {
                        return isPastedDate(value)
                    }
                }
                return false
            }),
            hour: string(),
            paymentType: string(),
            orderType: string(),
            amount: string().test(
                'isValidAmount',
                invalidKey,
                value => value === undefined || value.match(regex.AMOUNT)
            ),
            receipt: mixed()
                .test(
                    'isRequiredReceipt',
                    mandatory,
                    value =>
                        (selectedTheme.mandatoryReceipt && value) ||
                        !selectedTheme.mandatoryReceipt
                )
                .test(
                    'isValidFileSize',
                    'faq.form.error.file.size.file',
                    value => value === null || value.size < 2000000
                )
                .test(
                    'isValidFileType',
                    'faq.form.error.file.type',
                    value => value === null || value.extension.match(regex.FILE)
                )
                .when('subject', {
                    is: faqFormSubject.KINGDOM_POINTS,
                    then: mixed().required(mandatory)
                }),
            agreements: boolean()
                .required()
                .oneOf([true], mandatory)
        })
    }

    const renderExtDeliveryFaqMessage = () => {
        return (
            <StyledText center color={grey}>
                <FormattedMessage id="faq.form.order.external.delivery.message" />
            </StyledText>
        )
    }

    return success ? (
        <Main ref={myRef}>
            <Text>
                {intl.formatMessage({
                    id: 'faq.form.success.message'
                })}
            </Text>
            <ButtonHome>
                <StyledLink url={'/'} background={yellow} border="none">
                    {intl.formatMessage({
                        id: 'faq.form.success.button'
                    })}
                </StyledLink>
            </ButtonHome>
        </Main>
    ) : (
        <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={async (values, actions) => {
                let dateTime = values.date
                if (dateTime !== '' && values.hour !== '') {
                    dateTime = `${formatDate(dateTime)}${values.hour}`
                }
                const { receipt } = values
                const file = receipt ? receipt.file : ''
                const extension = receipt ? receipt.extension : ''
                const postedData = {
                    themeLabel: selectedTheme.label,
                    civility: values.civility,
                    lastname: values.lastname.trim(),
                    firstname: values.firstname.trim(),
                    address: values.address.trim(),
                    zipCode: values.zipCode.trim(),
                    city: values.city.trim(),
                    email: values.email.trim(),
                    loyaltyCardCode: values.loyaltyCardCode.trim(),
                    phone: values.phone,
                    subject: values.subject,
                    message: values.message.trim(),
                    note: values.note,
                    restaurant: values.restaurant,
                    date: dateTime,
                    paymentType: values.paymentType,
                    amount: values.amount,
                    orderType: values.orderType,
                    receipt: file,
                    receiptExtension: extension
                }
                try {
                    await dispatch(
                        sendFaqFormData(postedData, getPath(selectedTheme))
                    )
                } finally {
                    actions.setSubmitting(false)
                }
            }}
            validationSchema={formSubscription}
            render={({
                values,
                errors,
                touched,
                isSubmitting,
                handleChange,
                handleBlur,
                setFieldValue
            }) => (
                <Main>
                    <Form>
                        {selectedTheme.ecommerce && (
                            <HorizontalSection>
                                <SmallText color={grey}>
                                    {intl.formatMessage({
                                        id: 'form.ecommerce.mode.label'
                                    })}
                                </SmallText>
                                <div>
                                    <RadioContainer
                                        onClick={() => {
                                            selectEcommerceMode(
                                                CLICK_AND_COLLECT
                                            )
                                        }}>
                                        <CheckOrRadio
                                            name="ecommerceMode"
                                            type="radio"
                                            checked={isClickAndCollect}
                                            labelKey="clickandcollect">
                                            <Fragment>
                                                {isClickAndCollect ? (
                                                    <RadioChecked />
                                                ) : (
                                                    <EmptyCircle />
                                                )}
                                            </Fragment>
                                        </CheckOrRadio>
                                    </RadioContainer>
                                    <RadioContainer
                                        onClick={() => {
                                            selectEcommerceMode(DELIVERY_IN_APP)
                                        }}>
                                        <CheckOrRadio
                                            name="ecommerceMode"
                                            type="radio"
                                            checked={isDeliveryInApp}
                                            labelKey="faq.form.order.delivery.in.app">
                                            <Fragment>
                                                {isDeliveryInApp ? (
                                                    <RadioChecked />
                                                ) : (
                                                    <EmptyCircle />
                                                )}
                                            </Fragment>
                                        </CheckOrRadio>
                                    </RadioContainer>
                                    <RadioContainer
                                        onClick={() => {
                                            selectEcommerceMode(
                                                EXTERNAL_DELIVERY
                                            )
                                        }}>
                                        <CheckOrRadio
                                            name="ecommerceMode"
                                            type="radio"
                                            checked={isExternalDelivery}
                                            labelKey="faq.form.order.external.delivery">
                                            <Fragment>
                                                {isExternalDelivery ? (
                                                    <RadioChecked />
                                                ) : (
                                                    <EmptyCircle />
                                                )}
                                            </Fragment>
                                        </CheckOrRadio>
                                    </RadioContainer>
                                </div>
                            </HorizontalSection>
                        )}
                        {isExternalDelivery
                            ? renderExtDeliveryFaqMessage()
                            : (isClickAndCollect ||
                                  !selectedTheme.ecommerce) && (
                                  <Fragment>
                                      {selectedTheme.notation && (
                                          <Section>
                                              <SmallText color={grey}>
                                                  {intl.formatMessage({
                                                      id: 'faq.form.note.title'
                                                  })}
                                              </SmallText>
                                              {errors.note && touched.note && (
                                                  <Error
                                                      errorKey={errors.note}
                                                  />
                                              )}
                                              <Notes
                                                  notes={notesOptions}
                                                  value={values.note}
                                                  handleClick={setFieldValue}
                                              />
                                          </Section>
                                      )}
                                      <HorizontalSection center>
                                          <SmallText color={grey}>
                                              {intl.formatMessage({
                                                  id: 'faq.form.civility.title'
                                              })}
                                          </SmallText>
                                          <RadioContainer
                                              onClick={() =>
                                                  setFieldValue(
                                                      'civility',
                                                      woman
                                                  )
                                              }>
                                              <CheckOrRadio
                                                  name="civility"
                                                  type="radio"
                                                  checked={
                                                      values.civility === woman
                                                  }
                                                  labelKey={womanKey}>
                                                  <Fragment>
                                                      {values.civility ===
                                                      woman ? (
                                                          <RadioChecked />
                                                      ) : (
                                                          <EmptyCircle />
                                                      )}
                                                  </Fragment>
                                              </CheckOrRadio>
                                          </RadioContainer>
                                          <RadioContainer
                                              onClick={() =>
                                                  setFieldValue('civility', man)
                                              }>
                                              <CheckOrRadio
                                                  name="civility"
                                                  type="radio"
                                                  checked={
                                                      values.civility === man
                                                  }
                                                  labelKey={manKey}>
                                                  <Fragment>
                                                      {values.civility ===
                                                      man ? (
                                                          <RadioChecked />
                                                      ) : (
                                                          <EmptyCircle />
                                                      )}
                                                  </Fragment>
                                              </CheckOrRadio>
                                          </RadioContainer>
                                      </HorizontalSection>
                                      {errors.civility && touched.civility && (
                                          <Error errorKey={errors.civility} />
                                      )}
                                      <Section>
                                          <Input
                                              name="lastname"
                                              type="text"
                                              labelKey="faq.form.lastname.title"
                                              placeholderKey="form.lastname.placeholder"
                                              className={
                                                  errors.lastname &&
                                                  touched.lastname
                                                      ? 'error'
                                                      : ''
                                              }
                                          />
                                          {errors.lastname &&
                                              touched.lastname && (
                                                  <Error
                                                      errorKey={errors.lastname}
                                                  />
                                              )}
                                          <Input
                                              name="firstname"
                                              type="text"
                                              labelKey="form.firstname.title"
                                              placeholderKey="form.firstname.placeholder"
                                              className={
                                                  errors.firstname &&
                                                  touched.firstname
                                                      ? 'error'
                                                      : ''
                                              }
                                          />
                                          {errors.firstname &&
                                              touched.firstname && (
                                                  <Error
                                                      errorKey={
                                                          errors.firstname
                                                      }
                                                  />
                                              )}
                                          {selectedTheme.restaurant && (
                                              <Fragment>
                                                  <Input
                                                      name="address"
                                                      type="text"
                                                      labelKey="faq.form.address.title"
                                                      placeholderKey="faq.form.address.placeholder"
                                                      className={
                                                          errors.address &&
                                                          touched.address
                                                              ? 'error'
                                                              : ''
                                                      }
                                                  />
                                                  {errors.address &&
                                                      touched.address && (
                                                          <Error
                                                              errorKey={
                                                                  errors.address
                                                              }
                                                          />
                                                      )}
                                                  <InlineInputContainer>
                                                      <InlineInput width="20%">
                                                          <Input
                                                              name="zipCode"
                                                              type="text"
                                                              maxLength={5}
                                                              labelKey="faq.form.zipCode.title"
                                                              placeholderKey="faq.form.zipCode.placeholder"
                                                              className={
                                                                  errors.zipCode &&
                                                                  touched.zipCode
                                                                      ? 'error'
                                                                      : ''
                                                              }
                                                          />
                                                          {errors.zipCode &&
                                                              touched.zipCode && (
                                                                  <Error
                                                                      errorKey={
                                                                          errors.zipCode
                                                                      }
                                                                  />
                                                              )}
                                                      </InlineInput>
                                                      <InlineInput width="70%">
                                                          <Input
                                                              name="city"
                                                              type="text"
                                                              labelKey="faq.form.city.title"
                                                              placeholderKey="faq.form.city.placeholder"
                                                              className={
                                                                  errors.city &&
                                                                  touched.city
                                                                      ? 'error'
                                                                      : ''
                                                              }
                                                          />
                                                          {errors.city &&
                                                              touched.city && (
                                                                  <Error
                                                                      errorKey={
                                                                          errors.city
                                                                      }
                                                                  />
                                                              )}
                                                      </InlineInput>
                                                  </InlineInputContainer>
                                              </Fragment>
                                          )}
                                          <Input
                                              name="email"
                                              type="email"
                                              labelKey="faq.form.email.title"
                                              placeholderKey="form.email.placeholder"
                                              className={
                                                  errors.email && touched.email
                                                      ? 'error'
                                                      : ''
                                              }
                                          />
                                          {errors.email && touched.email && (
                                              <Error errorKey={errors.email} />
                                          )}
                                          {selectedTheme.kingdom && (
                                              <Fragment>
                                                  <Input
                                                      name="loyaltyCardCode"
                                                      type="text"
                                                      labelKey="faq.form.loyalty.title"
                                                      placeholderKey="form.loyalty.placeholder"
                                                      className={
                                                          errors.loyaltyCardCode &&
                                                          touched.loyaltyCardCode
                                                              ? 'error'
                                                              : ''
                                                      }
                                                  />
                                                  {errors.loyaltyCardCode &&
                                                      touched.loyaltyCardCode && (
                                                          <Error
                                                              errorKey={
                                                                  errors.loyaltyCardCode
                                                              }
                                                          />
                                                      )}
                                              </Fragment>
                                          )}
                                          {selectedTheme.kingdom && (
                                              <Fragment>
                                                  <SubjectContainer>
                                                      <SmallText color={grey}>
                                                          {intl.formatMessage({
                                                              id:
                                                                  'form.subject.title.label'
                                                          })}
                                                      </SmallText>
                                                      <RadioContainer
                                                          onClick={() =>
                                                              setFieldValue(
                                                                  'subject',
                                                                  faqFormSubject.KINGDOM_POINTS
                                                              )
                                                          }>
                                                          <CheckOrRadio
                                                              name="subject"
                                                              type="radio"
                                                              checked={
                                                                  values.subject ===
                                                                  faqFormSubject.KINGDOM_POINTS
                                                              }
                                                              labelKey={
                                                                  kingdomPointsSubjectKey
                                                              }>
                                                              <Fragment>
                                                                  {values.subject ===
                                                                  faqFormSubject.KINGDOM_POINTS ? (
                                                                      <RadioChecked />
                                                                  ) : (
                                                                      <EmptyCircle />
                                                                  )}
                                                              </Fragment>
                                                          </CheckOrRadio>
                                                      </RadioContainer>
                                                      <RadioContainer
                                                          onClick={() =>
                                                              setFieldValue(
                                                                  'subject',
                                                                  faqFormSubject.OTHER
                                                              )
                                                          }>
                                                          <CheckOrRadio
                                                              name="subject"
                                                              type="radio"
                                                              checked={
                                                                  values.subject ===
                                                                  faqFormSubject.OTHER
                                                              }
                                                              labelKey={
                                                                  otherSubjectKey
                                                              }>
                                                              <Fragment>
                                                                  {values.subject ===
                                                                  faqFormSubject.OTHER ? (
                                                                      <RadioChecked />
                                                                  ) : (
                                                                      <EmptyCircle />
                                                                  )}
                                                              </Fragment>
                                                          </CheckOrRadio>
                                                      </RadioContainer>
                                                  </SubjectContainer>
                                                  {errors.subject &&
                                                      touched.subject && (
                                                          <Error
                                                              errorKey={
                                                                  errors.subject
                                                              }
                                                          />
                                                      )}
                                              </Fragment>
                                          )}
                                          <Input
                                              name="phone"
                                              type="tel"
                                              labelKey="faq.form.phone.title"
                                              placeholderKey="form.phone.placeholder"
                                              className={
                                                  errors.phone && touched.phone
                                                      ? 'error'
                                                      : ''
                                              }
                                          />
                                          {errors.phone && touched.phone && (
                                              <Error errorKey={errors.phone} />
                                          )}
                                          <Textarea
                                              name="message"
                                              labelKey="faq.form.message.title"
                                              placeholderKey="faq.form.message.placeholder"
                                              className={
                                                  errors.message &&
                                                  touched.message
                                                      ? 'error'
                                                      : ''
                                              }
                                              handleChange={handleChange}
                                              handleBlur={handleBlur}
                                              value={values.message}
                                          />
                                          {errors.message &&
                                              touched.message && (
                                                  <Error
                                                      errorKey={errors.message}
                                                  />
                                              )}
                                      </Section>
                                      {selectedTheme.kingdom && (
                                          <ReceiptContainer
                                              onClick={() =>
                                                  fileUpload &&
                                                  fileUpload.click()
                                              }>
                                              {fileUploaded ? (
                                                  <Receipt
                                                      intl={intl}
                                                      labelKey="faq.form.receipt.loaded">
                                                      <CircleChecked />
                                                  </Receipt>
                                              ) : (
                                                  <Receipt
                                                      intl={intl}
                                                      labelKey="faq.form.receipt.placeholder">
                                                      <CirclePlus
                                                          background={yellow}
                                                          color="white"
                                                      />
                                                  </Receipt>
                                              )}
                                              <input
                                                  ref={input =>
                                                      (fileUpload = input)
                                                  }
                                                  onChange={e =>
                                                      handleFileChange(
                                                          e,
                                                          setFieldValue
                                                      )
                                                  }
                                                  name="receipt"
                                                  type="file"
                                                  accept="image/png, image/gif, image/jpeg, image/bmp, image/tiff, application/pdf"
                                              />
                                              {errors.receipt &&
                                                  touched.receipt && (
                                                      <Error
                                                          errorKey={
                                                              errors.receipt
                                                          }
                                                      />
                                                  )}
                                          </ReceiptContainer>
                                      )}
                                      {selectedTheme.restaurant && formData && (
                                          <SplittedSection>
                                              <Subsection>
                                                  <SmallText color={grey}>
                                                      {intl.formatMessage({
                                                          id:
                                                              'faq.form.restaurant.title'
                                                      })}
                                                  </SmallText>
                                                  <Select
                                                      name="department"
                                                      options={
                                                          formData.departments
                                                      }
                                                      handleChange={e =>
                                                          handleDepartmentChange(
                                                              e,
                                                              setFieldValue
                                                          )
                                                      }
                                                      setFieldValue={
                                                          setFieldValue
                                                      }
                                                      placeholderKey="faq.form.restaurant.department.placeholder"
                                                      className="department"
                                                  />
                                                  <Select
                                                      name="restaurant"
                                                      options={filterRestaurants(
                                                          formData.restaurants,
                                                          values.department
                                                      )}
                                                      handleChange={e =>
                                                          handleRestaurantChange(
                                                              e,
                                                              setFieldValue
                                                          )
                                                      }
                                                      setFieldValue={
                                                          setFieldValue
                                                      }
                                                      placeholderKey="faq.form.restaurant.placeholder"
                                                      className={`restaurant ${
                                                          errors.restaurant &&
                                                          touched.restaurant
                                                              ? 'error'
                                                              : ''
                                                      }`}
                                                  />
                                                  {errors.restaurant &&
                                                      touched.restaurant && (
                                                          <Error
                                                              errorKey={
                                                                  errors.restaurant
                                                              }
                                                          />
                                                      )}
                                              </Subsection>
                                              <Subsection>
                                                  <DateAndHour>
                                                      <Input
                                                          name="date"
                                                          type="date"
                                                          labelKey="faq.form.restaurant.date.title"
                                                          placeholderKey="form.date.placeholder"
                                                          className={`date ${
                                                              errors.date &&
                                                              touched.date
                                                                  ? 'error'
                                                                  : ''
                                                          }`}
                                                      />
                                                      <Select
                                                          name="hour"
                                                          options={hoursOptions}
                                                          handleChange={e => {
                                                              handleSelectChange(
                                                                  e,
                                                                  'hour',
                                                                  setFieldValue
                                                              )
                                                          }}
                                                          setFieldValue={
                                                              setFieldValue
                                                          }
                                                          className="hour"
                                                      />
                                                  </DateAndHour>
                                                  {errors.date &&
                                                      touched.date && (
                                                          <Error
                                                              errorKey={
                                                                  errors.date
                                                              }
                                                          />
                                                      )}
                                              </Subsection>
                                          </SplittedSection>
                                      )}
                                      {selectedTheme.order && formData && (
                                          <Fragment>
                                              <SplittedSection>
                                                  <Subsection>
                                                      <SmallText color={grey}>
                                                          {intl.formatMessage({
                                                              id:
                                                                  'faq.form.order.title'
                                                          })}
                                                      </SmallText>
                                                      <Select
                                                          name="paymentType"
                                                          options={
                                                              formData.paymentType
                                                          }
                                                          handleChange={e => {
                                                              handleSelectChange(
                                                                  e,
                                                                  'paymentType',
                                                                  setFieldValue
                                                              )
                                                          }}
                                                          setFieldValue={
                                                              setFieldValue
                                                          }
                                                          placeholderKey="faq.form.order.payment.placeholder"
                                                      />
                                                      <Select
                                                          name="orderType"
                                                          options={
                                                              formData.orderType
                                                          }
                                                          handleChange={e => {
                                                              handleSelectChange(
                                                                  e,
                                                                  'orderType',
                                                                  setFieldValue
                                                              )
                                                          }}
                                                          setFieldValue={
                                                              setFieldValue
                                                          }
                                                          placeholderKey="faq.form.order.type.placeholder"
                                                          className={
                                                              errors.orderType &&
                                                              touched.orderType
                                                                  ? 'error'
                                                                  : ''
                                                          }
                                                      />
                                                  </Subsection>
                                                  <Subsection>
                                                      <Input
                                                          name="amount"
                                                          type="string"
                                                          labelKey="faq.form.order.amount.title"
                                                          placeholderKey="faq.form.order.amount.placeholder"
                                                          className={`amount ${
                                                              errors.amount &&
                                                              touched.amount
                                                                  ? 'error'
                                                                  : ''
                                                          }`}
                                                      />
                                                      {errors.amount &&
                                                          touched.amount && (
                                                              <Error
                                                                  errorKey={
                                                                      errors.amount
                                                                  }
                                                              />
                                                          )}
                                                  </Subsection>
                                              </SplittedSection>
                                              {!!selectedTheme.receiptMention && (
                                                  <SmallText color={grey}>
                                                      {
                                                          selectedTheme.receiptMention
                                                      }
                                                  </SmallText>
                                              )}
                                              <ReceiptContainer
                                                  onClick={() =>
                                                      fileUpload &&
                                                      fileUpload.click()
                                                  }>
                                                  {fileUploaded ? (
                                                      <Receipt
                                                          intl={intl}
                                                          labelKey="faq.form.receipt.loaded">
                                                          <CircleChecked />
                                                      </Receipt>
                                                  ) : (
                                                      <Receipt
                                                          intl={intl}
                                                          labelKey="faq.form.receipt.placeholder">
                                                          <CirclePlus
                                                              background={
                                                                  yellow
                                                              }
                                                              color="white"
                                                          />
                                                      </Receipt>
                                                  )}
                                                  <input
                                                      ref={input =>
                                                          (fileUpload = input)
                                                      }
                                                      onChange={e =>
                                                          handleFileChange(
                                                              e,
                                                              setFieldValue
                                                          )
                                                      }
                                                      name="receipt"
                                                      type="file"
                                                      accept="image/png, image/gif, image/jpeg, image/bmp, image/tiff, application/pdf"
                                                  />
                                                  {errors.receipt &&
                                                      touched.receipt && (
                                                          <Error
                                                              errorKey={
                                                                  errors.receipt
                                                              }
                                                          />
                                                      )}
                                              </ReceiptContainer>
                                          </Fragment>
                                      )}
                                      <Agreements
                                          onClick={() =>
                                              setFieldValue(
                                                  'agreements',
                                                  !values.agreements
                                              )
                                          }>
                                          <CheckOrRadio
                                              name="agreements"
                                              type="checkbox"
                                              labelKey="faq.form.agreements"
                                              checked={values.agreements}
                                              className={`agreements ${
                                                  errors.agreements &&
                                                  touched.agreements
                                                      ? 'error'
                                                      : ''
                                              }`}>
                                              <Checkbox
                                                  checked={values.agreements}
                                                  color={
                                                      errors.agreements &&
                                                      touched.agreements &&
                                                      red
                                                  }
                                              />
                                          </CheckOrRadio>
                                      </Agreements>
                                      {errors.agreements &&
                                          touched.agreements && (
                                              <Error
                                                  errorKey={errors.agreements}
                                              />
                                          )}
                                      <Mentions>
                                          <TinyText color={grey}>
                                              {intl.formatMessage({
                                                  id:
                                                      'form.mandatory.information'
                                              })}
                                          </TinyText>
                                      </Mentions>
                                      <ButtonContainer>
                                          <StyledButtonLarge
                                              type="submit"
                                              background={yellow}
                                              disabled={isSubmitting}>
                                              <FormattedMessage id="form.button.send" />
                                          </StyledButtonLarge>
                                      </ButtonContainer>
                                      {sendFormError && (
                                          <Mentions>
                                              <Text color={red}>
                                                  {intl.formatMessage({
                                                      id: 'form.error.default'
                                                  })}
                                              </Text>
                                          </Mentions>
                                      )}
                                      <Mentions>
                                          <FormattedMessage id="faq.form.legalNotice.start">
                                              {(text: string) => (
                                                  <TinyText color={grey}>
                                                      {text}
                                                  </TinyText>
                                              )}
                                          </FormattedMessage>
                                          <MailLink
                                              href={`mailto:${legalNoticeMail}`}>
                                              <TinyText color={grey}>
                                                  {legalNoticeMail}
                                              </TinyText>
                                          </MailLink>
                                          <FormattedMessage id="faq.form.legalNotice.end">
                                              {(text: string) => (
                                                  <TinyText color={grey}>
                                                      {text}
                                                  </TinyText>
                                              )}
                                          </FormattedMessage>
                                          <LegaLNoticeLink
                                              url={`${intl.formatMessage({
                                                  id: 'routes.custom'
                                              })}/${intl.formatMessage({
                                                  id: 'routes.privacy'
                                              })}`}>
                                              <FormattedMessage id="faq.form.legalNotice.link">
                                                  {(text: string) => (
                                                      <TinyText color={grey}>
                                                          {text}
                                                      </TinyText>
                                                  )}
                                              </FormattedMessage>
                                          </LegaLNoticeLink>
                                      </Mentions>
                                  </Fragment>
                              )}
                    </Form>
                </Main>
            )}
        />
    )
}

const Main = styled.div`
    width: 100%;
    padding-top: 30px;
    padding-bottom: 30px;
    ${media.medium`
        margin: -40px auto 140px auto;
        background: white;
        border-radius: 10px;
        box-shadow: 0 8px 16px 4px rgba(48,49,51,.1);
        padding: 60px 105px;
        max-width: 850px;
    `};
`

const Section = styled.section`
    margin-bottom: 25px;
    display: flex;
    justify-content: flex-start;
    align-items: baseline;
    flex-direction: column;
    width: 100%;
`

const Subsection = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
`

const SplittedSection = styled(Section)`
    justify-content: flex-start;
    ${media.medium`
        flex-direction: row;
        justify-content: space-between;
    `};
`

const HorizontalSection = styled(Section)`
    display: flex;
    flex-direction: row;
    align-items: ${props => (props.center ? `center` : `flex-start`)};
`

const SubjectContainer = styled(Section)`
    align-items: center;
    margin-bottom: 0;
    margin-top: 20px;
    flex-direction: row;
`

const RadioContainer = styled.div`
    margin-left: 10px;
    font-size: 0;
    ${media.medium`
        margin-left: 25px;
    `};
`

const DateAndHour = styled(Subsection)`
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;
    position: relative;
`

const Mentions = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
`

const Agreements = styled(Section)`
    align-items: center;
    flex-direction: row;
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`

const ButtonHome = styled(ButtonContainer)`
    margin-top: 25px;
    margin-bottom: 25px;
`

const StyledButtonLarge = styled(ButtonLarge)`
    align-self: center;
    border: none;
`

const StyledLink = styled(Link)`
    ${style.buttonLarge};
`

const MailLink = styled.a`
    ${TinyText} {
        text-decoration: underline;
    }
`

const LegaLNoticeLink = styled(Link)`
    ${TinyText} {
        text-decoration: underline;
    }
`

const ReceiptContainer = styled.div`
    height: 110px;
    width: 100%;
    background-color: ${tertiary};
    cursor: pointer;
    align-self: center;
    border: none;
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    flex-direction: column;
    margin-bottom: 25px;
    border-radius: 10px;
    input {
        -webkit-appearance: none;
        outline: 0;
        display: none;
    }
`

const InlineInputContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    width: 100%;
`

const InlineInput = styled.div`
    width: 100%;
    ${media.medium`
        ${props => (props.width ? `width: ${props.width}` : '')};
    `};
`

const StyledText = styled(Text)`
    margin: 50px 0 20px;
`

export { FaqForm }
